import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import i18n, { setLanguage as applyLanguage, resolveInitialLanguage } from '../i18n';
import { useAuth } from './AuthContext';
import { users as usersApi } from '../services/api';

interface LanguageContextValue {
  language: string;
  setLanguage: (code: string) => void;
}

const LanguageContext = createContext<LanguageContextValue | undefined>(undefined);

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguageState] = useState<string>(
    () => i18n.language || resolveInitialLanguage(),
  );

  const setLanguage = useCallback((code: string) => {
    setLanguageState((prev) => {
      if (prev === code) return prev;
      // Load bundle + i18next.changeLanguage + persist localStorage.
      void applyLanguage(code);
      // Fire-and-forget server persist (matches ThemeContext behavior).
      usersApi.setLanguage(code).catch((err) => {
        console.warn('Failed to persist language selection', err);
      });
      return code;
    });
  }, []);

  const value = useMemo<LanguageContextValue>(
    () => ({ language, setLanguage }),
    [language, setLanguage],
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage(): LanguageContextValue {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return ctx;
}

/**
 * Lives inside the auth tree (next to ThemeSync). On first authenticated
 * render, pulls the server preferred language and reconciles with local:
 *  - server value present → adopt it (cross-device sync)
 *  - server value absent  → backfill with the local choice (one-time)
 */
export function LanguageSync() {
  const { user } = useAuth();
  const { language, setLanguage } = useLanguage();
  const syncedUserRef = useRef<string | null>(null);

  useEffect(() => {
    const currentUser = user?.userId ?? null;
    if (!currentUser) {
      syncedUserRef.current = null;
      return;
    }
    if (syncedUserRef.current === currentUser) return;
    syncedUserRef.current = currentUser;

    let cancelled = false;
    (async () => {
      try {
        const { language: serverLang } = await usersApi.getLanguage();
        if (cancelled) return;
        if (serverLang && serverLang !== language) {
          setLanguage(serverLang); // applies + re-persists (idempotent PUT)
        } else if (!serverLang) {
          await usersApi.setLanguage(language); // backfill
        }
      } catch (err) {
        console.warn('Language sync failed', err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user, language, setLanguage]);

  return null;
}
